<template>
    <table>
        <tr class="th-totals">
            <th class="referencia"></th>
            <th class="operacao"></th>
            <th class="hora"></th>
            <th class="tempo">{{$t('module-budget.main-budget.operations.operations-totals.time-hourlabor-th')}}</th>
            <th class="discount">{{$t('module-budget.main-budget.operations.operations-totals.discount-th')}}</th>
            <th class="total">{{$t('module-budget.main-budget.operations.operations-totals.sub-total-th')}}</th>
        </tr>

        <tr class="tr__registers" ref="operationsSubTotals">
            <td class="referencia"></td>
            <td class="operacao"></td>
            <td class="hora"></td>
            <td class="tempo">{{ `${this.computedTotalDuration.hours}h ${this.computedTotalDuration.minutes}m` }}</td>
            <td class="discount">
                <span v-if="isPreview">{{ totalDiscount }}</span>
                <v-text-field
                    v-else
                    v-model="totalDiscount"
                    type="number"
                    @keypress="onlyNumberRule"
                    min="0"
                    max="100"
                    :aria-label="$t('module-budget.main-budget.operations.operations-totals.discount-total-label')"
                    outlined
                    dense
                    hide-details
                    class="input-number"
                    @input="commitToStore"
                    :rules="descMaxRule"
                    @change="ruleDescMax(totalDiscount)"
                    data-cy = "discount-total-operations"
                >
                </v-text-field>
            </td>
            <td class="total" data-cy="operations-net-subtotal">
                {{ computedTotalWithoutDiscount | currency }}
            </td>
        </tr>

        <tr class="tr__registers">
            <td colspan="6" class="lasts-line-of-totals">
                <p>{{$t('module-budget.main-budget.operations.operations-totals.total-liquid-label')}}</p>
                <p ref="operationsNetSubTotalMO" data-cy="operations-net-total">
                    {{ computedTotalWithDiscount | currency }}
                </p>
            </td>
        </tr>
    </table>
</template>

<script>
import {hasPropertiesInObjectsOfArray} from '@src/utils/validations.js';

export default {
    props: {
        isPreview: { type: Boolean },
        /**
         * [{ hours: (number | string), minutes: (number | string)}, (...) ]
         */
        durations: {
            type: Array,
            required: true,
            validator: function(durations) {
                return hasPropertiesInObjectsOfArray(['hours', 'minutes'], durations);
            }
        },
        globalDiscount: { type: [Number, String], default: 0 }
    },
    data() {
        return {
            totalDiscount: this.globalDiscount,
            descMaxRule: [v => (v >= 0 && v <= 100) || 'Desc. max de 100%'],
            totalWithoutDiscountForTotalizerOE: 0
        };
    },
    computed: {
        computedTotalDuration() {
            // maybe we could use moment instead of doing these calculations

            let acc = property => (acc, currentValue) => acc + +currentValue[property];
            let totalHours = this.durations.reduce(acc('hours'), 0);
            let totalMinutes = this.durations.reduce(acc('minutes'), 0);

            // because the sum of minutes might end up like 130m, we need to subtract the hours from it and add it to the hours value
            totalHours += Math.floor(totalMinutes / 60);
            totalMinutes -= 60 * Math.floor(totalMinutes / 60);

            totalMinutes = String(totalMinutes)
            if(totalMinutes.length < 2) {
                totalMinutes = '0' + totalMinutes;
            }

            return {
                hours: totalHours,
                minutes: totalMinutes
            };
        },
        computedTotalWithoutDiscount() {
            const totalInCents = this.$store.state.budget.b2b.budgetSummary.operations.values.reduce((acc, row) => {
              if (!isNaN(row.totalWithoutVAT)) {
                return acc + Math.round(row.totalWithoutVAT * 100);
              } else {
                return acc;
              }
            }, 0);
            return totalInCents / 100;
        },
        computedTotalWithDiscount() {
            return this.totalDiscount > 0
                ? this.computedTotalWithoutDiscount * (1 - this.totalDiscount / 100)
                : this.computedTotalWithoutDiscount;
        },
        computedStateForTotalizersCalcsOE() {
            this.totalWithoutDiscountForTotalizerOE = this.$store.state.budget.b2b.budgetSummary.stateForTotalizersCalcsOE.reduce((acc, row) => acc + +row.totalWithoutVAT, 0);
            return this.$store.state.budget.b2b.budgetSummary.stateForTotalizersCalcsOE;
        },
        computedTotalWithDiscountForTotalizerOE() {
            return this.totalDiscount > 0
                ? this.totalWithoutDiscountForTotalizerOE * (1 - this.totalDiscount / 100)
                : this.totalWithoutDiscountForTotalizerOE;

        },
    },
    watch: {
        computedTotalWithoutDiscount: {
            handler: function(newValue, oldValue) {
                this.commitToStore();
            },
            deep: true
        },
        computedStateForTotalizersCalcsOE: {
            handler: function(newValue, oldValue) {
                this.commitToStore();
            }, deep: true, immediate: true
        }
    },
    methods: {
        commitToStore() {
            const operationsTotals = {
                totalDuration: this.computedTotalDuration,
                globalDiscount: this.totalDiscount,
                totalWithoutDiscount: this.$options.filters.currency(+this.computedTotalWithoutDiscount, true, false),
                totalWithDiscount: this.$options.filters.currency(+this.computedTotalWithDiscount, true, false)
            };
            this.$store.commit('budget/setOperationTotalsToBudgetSummary', operationsTotals);
            this.$store.commit('budget/setShoppingCartTotals', {isOp: true, totals: operationsTotals });

            let totalWithoutDiscount = [];
            let totalWithDiscount = [];
            this.$store.state.budget.b2b.budgetSummary.operations.values.forEach(op => {
              const newFinalValue = +op.hourValueDefault.oe.labor * (+op.duration.hours + +op.duration.minutes / 60);
              const newValuePrice = +op.discount > 0 ? newFinalValue * (1 - op.discount / 100) : newFinalValue;
              totalWithDiscount.push(this.$options.filters.currency(newValuePrice, true, false));
              totalWithoutDiscount.push(this.$options.filters.currency(newFinalValue, true, false));
            });

            let theTotalWithDiscount = +totalWithDiscount.reduce((acc, value) => acc + value, 0)
            if(this.totalDiscount > 0) {
              theTotalWithDiscount = theTotalWithDiscount * (1 - this.totalDiscount / 100)
            }

            const operationTotalsForTotalizerOE = {
                totalDuration: this.computedTotalDuration,
                globalDiscount: this.totalDiscount,
                totalWithoutDiscount: this.$options.filters.currency(+totalWithoutDiscount.reduce((acc, value) => acc + value, 0), true, false),
                totalWithDiscount: this.$options.filters.currency(theTotalWithDiscount, true, false)
            };
            this.$store.commit('budget/setBudgetPayloadForTotalizersTotalsCalcsOE', operationTotalsForTotalizerOE);
            this.$emit('operationTotalsForTotalizerOE', operationTotalsForTotalizerOE);

        },
        ruleDescMax(value) {
            if (value > 100) {
                this.totalDiscount = 100;
                this.commitToStore();
            }else if(value < 0){
                this.totalDiscount = 0;
                this.commitToStore();
            }
        }
    },
    mounted() {
        this.commitToStore();
    }
};
</script>

<style lang="scss" scoped>
@import './table-style.scss';

.input-number{
    ::v-deep input{
        text-align: right;
    }
}
</style>
